import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ModifiedDateFilter } from '~/components/Filters/DateFilter/ModifiedDateFilter';
import { SingleFilterContainer } from '~/components/Filters/SingleFilters/SingleFilterContainer';
import { type SingleFilterProps } from '~/components/Filters/types';
import {
  setModifiedDateFilterEndDateAction,
  setModifiedDateFilterStartDateAction,
  setModifiedDateFilterTypeAction,
} from '~/store/filters/actions';
import {
  modifiedDateFilterDateTypeSelector,
  modifiedDateFilterEndDateSelector,
  modifiedDateFilterStartDateSelector,
} from '~/store/filters/selectors';
import { type DateRangeType } from '~/store/filters/types';

export const SingleModifiedDateFilter = memo(({ onApplyClick }: SingleFilterProps) => {
  const dispatch = useDispatch();
  const dateType = useSelector(modifiedDateFilterDateTypeSelector);
  const startDate = useSelector(modifiedDateFilterStartDateSelector);
  const endDate = useSelector(modifiedDateFilterEndDateSelector);

  const onDateTypeChange = useCallback(
    (type: DateRangeType) => {
      dispatch(setModifiedDateFilterTypeAction({ type }));
      onApplyClick();
    },
    [dispatch, onApplyClick],
  );

  const onStartDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (Array.isArray(date)) return;
      dispatch(setModifiedDateFilterStartDateAction({ startDate: date }));
      onApplyClick();
    },
    [dispatch, onApplyClick],
  );

  const onEndDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (Array.isArray(date)) return;
      dispatch(setModifiedDateFilterEndDateAction({ endDate: date }));
      onApplyClick();
    },
    [dispatch, onApplyClick],
  );

  return (
    <SingleFilterContainer currentFilter="dateModified" onApplyClick={onApplyClick}>
      <ModifiedDateFilter
        startDate={startDate}
        endDate={endDate}
        dateType={dateType}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        onTypeChange={onDateTypeChange}
      />
    </SingleFilterContainer>
  );
});

SingleModifiedDateFilter.displayName = 'SingleModifiedDateFilter';
